import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";
import { applyMetadataSave, buildMetadataSaveBody, metadataChangeLine, metadataDraftNeedsReset, leavePageWarning, phraseRows, draftChangeNames, draftChangeDetails, storedBlankPhraseEntries, storedBlankPhraseRemoveLabel } from "./roofingV2MetadataSave";
import { applyReloadedEnvelope, reloadOutcome } from "./roofingV2Reload";
import { roofingV2ScheduleView, scheduleEmergencyNotice, scheduleExpiryNotice, scheduleReopenNotice, scheduleSendNotice, scheduleTimeNotices, weekdayLabel } from "./roofingV2ScheduleView";
import { configuredListHeading, handoffCancelNotice, handoffDeadlineNotice, roofingV2HandoffView } from "./roofingV2HandoffView";
import { roofingV2CapabilityView } from "./roofingV2CapabilityView";
import { roofingV2ChannelView } from "./roofingV2ChannelView";
import { releaseSloText, roofingV2DataView } from "./roofingV2DataView";
import { knowledgeCustomerPreview, knowledgeWindowLabel, roofingV2KnowledgeView } from "./roofingV2KnowledgeView";
import { buildKnowledgeDraftBody, knowledgeDraftSaveFollowUp, resolveKnowledgeDraftCommand } from "./roofingV2KnowledgeDraft";
import { buildKnowledgePublishBody, knowledgePublishSaveFollowUp, resolveKnowledgePublishCommand } from "./roofingV2KnowledgePublish";
import { buildKnowledgeWithdrawBody, knowledgeWithdrawSaveFollowUp, resolveKnowledgeWithdrawCommand } from "./roofingV2KnowledgeWithdraw";
import { buildKnowledgeSourceBody, knowledgeSourceSaveFollowUp, resolveKnowledgeSourceCommand } from "./roofingV2KnowledgeSource";
import { KNOWLEDGE_INVALIDATION_UNREAD, knowledgeInvalidationNotice } from "./roofingV2KnowledgeInvalidation";
import { roofingV2MediaView } from "./roofingV2MediaView";
import { activationValueText, roofingV2QualificationView } from "./roofingV2QualificationView";
import { roofingV2ServiceAreaView } from "./roofingV2ServiceAreaView";
import { documentCurrency, minorDecimal } from "./roofingV2MoneyView";
import { reasonCode, reasonLine, derivedModeAccepted, readinessNotice, readinessCheckTimeNotice, readinessExpiryNotice, readinessCandidateNotice, readinessAvailabilityNotice, readinessChatNotice, readinessSnapshotNotice, readinessProviderNotice, readinessAuditNotice, readinessMissingNotice, readinessAdmissionNotice, readinessRegisterNotice, readinessDistinctionNotice, readinessBypassNotice, readinessPauseNotice, readinessLinesFromResult } from "./roofingV2ModeReasons";
import { noticeNeedsFocus } from "./roofingV2NoticeFocus";
import { saveProgressLabel } from "./roofingV2SaveProgress";
import { revisionText, schemaVersionText, specRevisionText, botTypeText, userIdText, copyRevisionSiblings } from "./roofingV2RevisionView";
import { phraseFieldLabels } from "./roofingV2PhraseLabels";
import { phraseRowLayout } from "./roofingV2PhraseLayout";
import { settingsColumnLayout } from "./roofingV2SettingsColumn";

function acceptedMode(mode) {
  return derivedModeAccepted(mode);
}

function snapshotFromEnvelope(envelope) {
  const settings = copyRevisionSiblings((envelope && envelope.settings) || {}, envelope);
  delete settings.credential_refs;
  return {
    settings,
    derivedMode: envelope && envelope.derived_mode,
  };
}

function draftFromSettings(settings) {
  return {
    brand: settings.brand || "",
    tone: settings.tone || "",
    rows: phraseRows(settings.phrase_settings).map((row) => ({
      id: crypto.randomUUID(),
      key: row.key,
      value: row.value,
    })),
    omittedBlankKeys: [],
  };
}

function moneyText(minor, currency) {
  const decimal = minorDecimal(minor, currency);
  if (decimal == null) {
    return String(minor);
  }
  return `${String(minor)} (${decimal} ${currency})`;
}

export default function RoofingV2BotSettings({ envelope }) {
  const [snapshot, setSnapshot] = useState(() => snapshotFromEnvelope(envelope));
  const [draft, setDraft] = useState(() => draftFromSettings(snapshotFromEnvelope(envelope).settings));
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [notice, setNotice] = useState("");
  const [reloadProblem, setReloadProblem] = useState("");
  const [knowledge, setKnowledge] = useState(null);
  const [knowledgeProblem, setKnowledgeProblem] = useState("");
  const [readinessLines, setReadinessLines] = useState(null);
  const readinessLoad = useRef(0);
  const [knowledgeDraft, setKnowledgeDraft] = useState({
    factId: "",
    revision: "",
    topicKey: "",
    text: "",
    language: "",
    audience: "",
    ownerRef: "",
    expectedRevision: "",
  });
  const [knowledgeCommand, setKnowledgeCommand] = useState(null);
  const [knowledgeDraftNotice, setKnowledgeDraftNotice] = useState("");
  const [knowledgeDraftSaving, setKnowledgeDraftSaving] = useState(false);
  const [knowledgePublish, setKnowledgePublish] = useState({
    factId: "",
    revision: "",
    topicKey: "",
    text: "",
    language: "",
    audience: "",
    ownerRef: "",
    sourceRef: "",
    sourceRevision: "",
    approvedBy: "",
    reviewerRef: "",
    approvedAt: "",
    validFrom: "",
    validUntil: "",
    expectedRevision: "",
  });
  const [knowledgePublishCommand, setKnowledgePublishCommand] = useState(null);
  const [knowledgePublishNotice, setKnowledgePublishNotice] = useState("");
  const [knowledgePublishSaving, setKnowledgePublishSaving] = useState(false);
  const [knowledgeWithdraw, setKnowledgeWithdraw] = useState({
    factId: "",
    revision: "",
    topicKey: "",
    text: "",
    language: "",
    audience: "",
    ownerRef: "",
    sourceRef: "",
    sourceRevision: "",
    expectedRevision: "",
  });
  const [knowledgeWithdrawCommand, setKnowledgeWithdrawCommand] = useState(null);
  const [knowledgeWithdrawNotice, setKnowledgeWithdrawNotice] = useState("");
  const [knowledgeWithdrawSaving, setKnowledgeWithdrawSaving] = useState(false);
  const [knowledgeSource, setKnowledgeSource] = useState({
    factId: "",
    revision: "",
    topicKey: "",
    text: "",
    language: "",
    audience: "",
    ownerRef: "",
    sourceRef: "",
    sourceRevision: "",
    approvedBy: "",
    reviewerRef: "",
    approvedAt: "",
    validFrom: "",
    validUntil: "",
    expectedRevision: "",
  });
  const [knowledgeSourceCommand, setKnowledgeSourceCommand] = useState(null);
  const [knowledgeSourceNotice, setKnowledgeSourceNotice] = useState("");
  const [knowledgeSourceSaving, setKnowledgeSourceSaving] = useState(false);
  const noticeRef = useRef(null);

  useEffect(() => {
    const next = snapshotFromEnvelope(envelope);
    setSnapshot(next);
    setDraft(draftFromSettings(next.settings));
    setNotice("");
    setReloadProblem("");
  }, [envelope]);

  const mode = acceptedMode(snapshot.derivedMode);
  const schedule = roofingV2ScheduleView(snapshot.settings);
  const sharedCurrency = documentCurrency(schedule.appointmentTypes);
  const serviceArea = roofingV2ServiceAreaView(snapshot.settings);
  const qualification = roofingV2QualificationView(snapshot.settings);
  const handoff = roofingV2HandoffView(snapshot.settings);
  const targetsHeading = configuredListHeading("targets", handoff.targetsStatus, handoff.targets);
  const triggersHeading = configuredListHeading("triggers", handoff.triggersStatus, handoff.triggers);
  const routesHeading = configuredListHeading(
    "escalation_routes",
    handoff.escalationRoutesStatus,
    handoff.escalationRoutes,
  );
  const messagesHeading = configuredListHeading("messages", handoff.messagesStatus, handoff.messages);
  const windowsHeading = configuredListHeading("windows", handoff.windowsStatus, handoff.windows);
  const officeWindowsHeading = configuredListHeading(
    "office_hours.windows",
    schedule.officeHours.windowsStatus,
    schedule.officeHours.windows,
  );
  const botWindowsHeading = configuredListHeading(
    "bot_schedule.windows",
    schedule.botSchedule.windowsStatus,
    schedule.botSchedule.windows,
  );
  const appointmentTypesHeading = configuredListHeading(
    "appointment_types",
    schedule.appointmentTypesStatus,
    schedule.appointmentTypes,
  );
  const servicesHeading = configuredListHeading("services", serviceArea.servicesStatus, serviceArea.services);
  const questionsHeading = configuredListHeading(
    "questions",
    qualification.questionsStatus,
    qualification.questions,
  );
  const rulesHeading = configuredListHeading("dq_rules", qualification.rulesStatus, qualification.rules);
  const rejectionMessagesHeading = configuredListHeading(
    "rejection_messages",
    qualification.rejectionMessagesStatus,
    qualification.rejectionMessages,
  );
  const media = roofingV2MediaView(snapshot.settings);
  const mediaLimitsHeading = configuredListHeading(
    "accepted_media_limits",
    media.limitsStatus,
    media.limits,
  );
  const channel = roofingV2ChannelView(snapshot.settings);
  const capabilitiesHeading = configuredListHeading(
    "notification_capabilities",
    channel.capabilitiesStatus,
    channel.capabilities,
  );
  const dataProfile = roofingV2DataView(snapshot.settings);
  const budgetsHeading = configuredListHeading("budgets", dataProfile.budgetsStatus, dataProfile.budgets);
  const capability = roofingV2CapabilityView(snapshot.settings);

  function applyReloadOutcome(noticeAtStart, result) {
    const outcome = reloadOutcome(noticeAtStart, result);
    setNotice(outcome.notice);
    setReloadProblem(outcome.reloadProblem);
  }

  function loadReadinessLines() {
    const loadId = readinessLoad.current + 1;
    readinessLoad.current = loadId;
    const tokenAtStart = getClientAuthToken();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    setReadinessLines(null);
    axios
      .get(`${backendUrl}/api/roofing_v2/readiness`, {
        headers: getClientAuthHeaders(),
      })
      .then((res) => {
        if (loadId !== readinessLoad.current || getClientAuthToken() !== tokenAtStart) {
          return;
        }
        setReadinessLines(readinessLinesFromResult(res.data));
      })
      .catch(() => {
        if (loadId !== readinessLoad.current || getClientAuthToken() !== tokenAtStart) {
          return;
        }
        setReadinessLines(null);
      });
  }

  async function onReload() {
    if (saving || reloading) return;
    const tokenAtStart = getClientAuthToken();
    const noticeAtStart = notice;
    setReloadProblem("");
    setReloading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.get(`${backendUrl}/api/bot/settings`, {
        headers: getClientAuthHeaders(),
      });
      if (getClientAuthToken() !== tokenAtStart) {
        applyReloadOutcome(noticeAtStart, "stale_token");
        return;
      }
      const applied = applyReloadedEnvelope(res.data);
      if (!applied.accepted) {
        applyReloadOutcome(noticeAtStart, "rejected");
        return;
      }
      setSnapshot({ settings: applied.settings, derivedMode: applied.derivedMode });
      loadReadinessLines();
      applyReloadOutcome(noticeAtStart, "accepted");
    } catch {
      const result = getClientAuthToken() !== tokenAtStart ? "stale_token" : "failed";
      applyReloadOutcome(noticeAtStart, result);
    } finally {
      setReloading(false);
    }
  }

  async function onSave() {
    if (reloading) return;
    const phraseSettings = {};
    const seen = new Set();
    for (const row of draft.rows) {
      if (seen.has(row.key)) {
        setNotice("Phrase keys must be unique.");
        return;
      }
      seen.add(row.key);
      phraseSettings[row.key] = row.value;
    }
    const commandId = crypto.randomUUID();
    const body = buildMetadataSaveBody({
      settings: snapshot.settings,
      draft: {
        brand: draft.brand,
        tone: draft.tone,
        phrase_settings: phraseSettings,
        omittedBlankKeys: draft.omittedBlankKeys,
      },
      commandId,
    });
    if (!body || body.error) {
      if (body && body.error === "revision") {
        setNotice("settings_revision is not configured.");
      } else if (body && body.error === "schema") {
        setNotice("schema_version is not configured.");
      } else if (body && body.error) {
        setNotice("Brand, tone, and phrase keys cannot be empty.");
      }
      return;
    }
    setSaving(true);
    setNotice("");
    setReloadProblem("");
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(
        `${backendUrl}/api/bot/save_settings/roofing_v2_bot`,
        body,
        { headers: getClientAuthHeaders() }
      );
      const applied = applyMetadataSave(snapshot, res.data);
      if (!applied.accepted) {
        setNotice("The server response was not accepted.");
        return;
      }
      setSnapshot({ settings: applied.settings, derivedMode: applied.derivedMode });
      loadReadinessLines();
      setDraft(draftFromSettings(applied.settings));
    } catch (err) {
      const status = err.response && err.response.status;
      const detail = err.response && err.response.data && err.response.data.detail;
      if (
        status === 409
        && (detail === "settings_revision conflict" || detail === "fence change is not durable yet")
      ) {
        setNotice(detail);
      } else if (status === 422) {
        setNotice("Invalid roofing_v2 settings");
      } else {
        setNotice("Settings could not be saved.");
      }
    } finally {
      setSaving(false);
    }
  }

  const blankPhraseEntriesView = storedBlankPhraseEntries(
    snapshot.settings && snapshot.settings.phrase_settings,
    draft.omittedBlankKeys,
  );
  const changeNames = draftChangeNames(snapshot.settings, draft);
  const changeDetails = draftChangeDetails(snapshot.settings, draft);
  const needsReset = metadataDraftNeedsReset(snapshot.settings, draft);
  const progressLabel = saveProgressLabel(saving, reloading);

  useEffect(() => {
    const warning = leavePageWarning(needsReset);
    if (!warning) {
      return undefined;
    }
    function onLeave(event) {
      event.preventDefault();
      event.returnValue = warning;
    }
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [needsReset]);

  useEffect(() => {
    if (!noticeNeedsFocus(notice, reloadProblem) || !noticeRef.current) {
      return undefined;
    }
    noticeRef.current.focus();
    return undefined;
  }, [notice, reloadProblem]);

  const showSettings = Boolean(mode);

  useEffect(() => {
    if (!showSettings) {
      return undefined;
    }
    let cancelled = false;
    const tokenAtStart = getClientAuthToken();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    setKnowledgeProblem("");
    axios
      .get(`${backendUrl}/api/roofing_v2/knowledge`, {
        headers: getClientAuthHeaders(),
      })
      .then((res) => {
        if (cancelled || getClientAuthToken() !== tokenAtStart) {
          return;
        }
        setKnowledge(roofingV2KnowledgeView(res.data));
        setKnowledgeProblem("");
      })
      .catch(() => {
        if (cancelled || getClientAuthToken() !== tokenAtStart) {
          return;
        }
        setKnowledge(null);
        setKnowledgeProblem("Knowledge could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [showSettings]);

  useEffect(() => {
    if (!showSettings) {
      return undefined;
    }
    loadReadinessLines();
    return () => {
      readinessLoad.current += 1;
    };
  }, [showSettings]);

  function updateKnowledgeDraft(key, value) {
    setKnowledgeDraft((current) => ({ ...current, [key]: value }));
  }

  async function onSaveKnowledgeDraft(event) {
    event.preventDefault();
    if (knowledgeDraftSaving) return;
    const command = resolveKnowledgeDraftCommand(knowledgeCommand, knowledgeDraft, () => ({
      commandId: crypto.randomUUID(),
      asOf: new Date().toISOString(),
    }));
    const built = buildKnowledgeDraftBody({
      commandId: command.commandId,
      asOf: command.asOf,
      fields: knowledgeDraft,
    });
    if (built.error) {
      setKnowledgeDraftNotice(
        "Fact id, revision, topic, language, audience, and owner cannot be empty.",
      );
      return;
    }
    setKnowledgeCommand(command);
    const tokenAtStart = getClientAuthToken();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    setKnowledgeDraftSaving(true);
    setKnowledgeDraftNotice("");
    try {
      let outcome = null;
      try {
        const res = await axios.post(`${backendUrl}/api/roofing_v2/knowledge`, built.body, {
          headers: getClientAuthHeaders(),
        });
        if (getClientAuthToken() !== tokenAtStart) return;
        outcome = res.data && res.data.outcome;
      } catch (error) {
        if (getClientAuthToken() !== tokenAtStart) return;
        const status = error.response && error.response.status;
        const detail = error.response && error.response.data && error.response.data.detail;
        const followUp = knowledgeDraftSaveFollowUp({ status, detail });
        if (!followUp.reload) {
          setKnowledgeDraftNotice(followUp.notice);
          return;
        }
        try {
          const listed = await axios.get(`${backendUrl}/api/roofing_v2/knowledge`, {
            headers: getClientAuthHeaders(),
          });
          if (getClientAuthToken() !== tokenAtStart) return;
          setKnowledgeDraftNotice(followUp.notice);
          setKnowledge(roofingV2KnowledgeView(listed.data));
          setKnowledgeProblem("");
        } catch {
          if (getClientAuthToken() !== tokenAtStart) return;
          const failed = knowledgeDraftSaveFollowUp({ status, detail, reloadFailed: true });
          setKnowledgeDraftNotice(failed.notice);
        }
        return;
      }
      const accepted = knowledgeDraftSaveFollowUp({ outcome });
      if (!accepted.saved) {
        setKnowledgeDraftNotice(accepted.notice);
        return;
      }
      try {
        const listed = await axios.get(`${backendUrl}/api/roofing_v2/knowledge`, {
          headers: getClientAuthHeaders(),
        });
        if (getClientAuthToken() !== tokenAtStart) return;
        const followUp = knowledgeDraftSaveFollowUp({ outcome });
        setKnowledgeDraftNotice(followUp.notice);
        if (followUp.replaceList) {
          setKnowledge(roofingV2KnowledgeView(listed.data));
          setKnowledgeProblem("");
        }
      } catch {
        if (getClientAuthToken() !== tokenAtStart) return;
        const followUp = knowledgeDraftSaveFollowUp({ outcome, reloadFailed: true });
        setKnowledgeDraftNotice(followUp.notice);
      }
    } finally {
      setKnowledgeDraftSaving(false);
    }
  }

  async function readInvalidationSentence(backendUrl, tokenAtStart, jobId) {
    if (typeof jobId !== "string" || jobId === "") {
      return "";
    }
    try {
      const job = await axios.get(
        `${backendUrl}/api/roofing_v2/knowledge/jobs/${encodeURIComponent(jobId)}`,
        { headers: getClientAuthHeaders() },
      );
      if (getClientAuthToken() !== tokenAtStart) {
        return null;
      }
      return knowledgeInvalidationNotice(job.data && job.data.job_status);
    } catch {
      if (getClientAuthToken() !== tokenAtStart) {
        return null;
      }
      return KNOWLEDGE_INVALIDATION_UNREAD;
    }
  }

  function updateKnowledgePublish(key, value) {
    setKnowledgePublish((current) => ({ ...current, [key]: value }));
  }

  async function onPublishKnowledge(event) {
    event.preventDefault();
    if (knowledgePublishSaving) return;
    const command = resolveKnowledgePublishCommand(knowledgePublishCommand, knowledgePublish, () => ({
      commandId: crypto.randomUUID(),
      asOf: new Date().toISOString(),
    }));
    const userId = snapshot.settings && snapshot.settings.user_id;
    const built = buildKnowledgePublishBody({
      commandId: command.commandId,
      asOf: command.asOf,
      fields: knowledgePublish,
      userId,
    });
    if (built.error === "reviewer") {
      setKnowledgePublishNotice("The reviewer must be different from the signed-in user.");
      return;
    }
    if (built.error) {
      setKnowledgePublishNotice("Publish fields are incomplete or the review window is invalid.");
      return;
    }
    setKnowledgePublishCommand(command);
    const tokenAtStart = getClientAuthToken();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    setKnowledgePublishSaving(true);
    setKnowledgePublishNotice("");
    try {
      let outcome = null;
      let jobId = "";
      try {
        const res = await axios.post(
          `${backendUrl}/api/roofing_v2/knowledge/${encodeURIComponent(built.body.fact.fact_id)}/publish`,
          built.body,
          { headers: getClientAuthHeaders() },
        );
        if (getClientAuthToken() !== tokenAtStart) return;
        outcome = res.data && res.data.outcome;
        if (res.data && typeof res.data.job_id === "string") {
          jobId = res.data.job_id;
        }
      } catch (error) {
        if (getClientAuthToken() !== tokenAtStart) return;
        const status = error.response && error.response.status;
        const detail = error.response && error.response.data && error.response.data.detail;
        const followUp = knowledgePublishSaveFollowUp({ status, detail });
        if (!followUp.reload) {
          setKnowledgePublishNotice(followUp.notice);
          return;
        }
        try {
          const listed = await axios.get(`${backendUrl}/api/roofing_v2/knowledge`, {
            headers: getClientAuthHeaders(),
          });
          if (getClientAuthToken() !== tokenAtStart) return;
          setKnowledgePublishNotice(followUp.notice);
          setKnowledge(roofingV2KnowledgeView(listed.data));
          setKnowledgeProblem("");
        } catch {
          if (getClientAuthToken() !== tokenAtStart) return;
          const failed = knowledgePublishSaveFollowUp({ status, detail, reloadFailed: true });
          setKnowledgePublishNotice(failed.notice);
        }
        return;
      }
      const accepted = knowledgePublishSaveFollowUp({ outcome });
      if (!accepted.saved) {
        setKnowledgePublishNotice(accepted.notice);
        return;
      }
      let notice = accepted.notice;
      try {
        const listed = await axios.get(`${backendUrl}/api/roofing_v2/knowledge`, {
          headers: getClientAuthHeaders(),
        });
        if (getClientAuthToken() !== tokenAtStart) return;
        const followUp = knowledgePublishSaveFollowUp({ outcome });
        notice = followUp.notice;
        if (followUp.replaceList) {
          setKnowledge(roofingV2KnowledgeView(listed.data));
          setKnowledgeProblem("");
        }
      } catch {
        if (getClientAuthToken() !== tokenAtStart) return;
        notice = knowledgePublishSaveFollowUp({ outcome, reloadFailed: true }).notice;
      }
      const sentence = await readInvalidationSentence(backendUrl, tokenAtStart, jobId);
      if (sentence == null) return;
      setKnowledgePublishNotice(sentence === "" ? notice : `${notice} ${sentence}`);
    } finally {
      setKnowledgePublishSaving(false);
    }
  }

  function updateKnowledgeWithdraw(key, value) {
    setKnowledgeWithdraw((current) => ({ ...current, [key]: value }));
  }

  async function onWithdrawKnowledge(event) {
    event.preventDefault();
    if (knowledgeWithdrawSaving) return;
    const command = resolveKnowledgeWithdrawCommand(knowledgeWithdrawCommand, knowledgeWithdraw, () => ({
      commandId: crypto.randomUUID(),
      asOf: new Date().toISOString(),
    }));
    const built = buildKnowledgeWithdrawBody({
      commandId: command.commandId,
      asOf: command.asOf,
      fields: knowledgeWithdraw,
    });
    if (built.error) {
      setKnowledgeWithdrawNotice("Withdraw fields cannot be empty.");
      return;
    }
    setKnowledgeWithdrawCommand(command);
    const tokenAtStart = getClientAuthToken();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    setKnowledgeWithdrawSaving(true);
    setKnowledgeWithdrawNotice("");
    try {
      let outcome = null;
      let jobId = "";
      try {
        const res = await axios.post(
          `${backendUrl}/api/roofing_v2/knowledge/${encodeURIComponent(built.body.fact.fact_id)}/withdraw`,
          built.body,
          { headers: getClientAuthHeaders() },
        );
        if (getClientAuthToken() !== tokenAtStart) return;
        outcome = res.data && res.data.outcome;
        if (res.data && typeof res.data.job_id === "string") {
          jobId = res.data.job_id;
        }
      } catch (error) {
        if (getClientAuthToken() !== tokenAtStart) return;
        const status = error.response && error.response.status;
        const detail = error.response && error.response.data && error.response.data.detail;
        const followUp = knowledgeWithdrawSaveFollowUp({ status, detail });
        if (!followUp.reload) {
          setKnowledgeWithdrawNotice(followUp.notice);
          return;
        }
        try {
          const listed = await axios.get(`${backendUrl}/api/roofing_v2/knowledge`, {
            headers: getClientAuthHeaders(),
          });
          if (getClientAuthToken() !== tokenAtStart) return;
          setKnowledgeWithdrawNotice(followUp.notice);
          setKnowledge(roofingV2KnowledgeView(listed.data));
          setKnowledgeProblem("");
        } catch {
          if (getClientAuthToken() !== tokenAtStart) return;
          const failed = knowledgeWithdrawSaveFollowUp({ status, detail, reloadFailed: true });
          setKnowledgeWithdrawNotice(failed.notice);
        }
        return;
      }
      const accepted = knowledgeWithdrawSaveFollowUp({ outcome });
      if (!accepted.saved) {
        setKnowledgeWithdrawNotice(accepted.notice);
        return;
      }
      let notice = accepted.notice;
      try {
        const listed = await axios.get(`${backendUrl}/api/roofing_v2/knowledge`, {
          headers: getClientAuthHeaders(),
        });
        if (getClientAuthToken() !== tokenAtStart) return;
        const followUp = knowledgeWithdrawSaveFollowUp({ outcome });
        notice = followUp.notice;
        if (followUp.replaceList) {
          setKnowledge(roofingV2KnowledgeView(listed.data));
          setKnowledgeProblem("");
        }
      } catch {
        if (getClientAuthToken() !== tokenAtStart) return;
        notice = knowledgeWithdrawSaveFollowUp({ outcome, reloadFailed: true }).notice;
      }
      const sentence = await readInvalidationSentence(backendUrl, tokenAtStart, jobId);
      if (sentence == null) return;
      setKnowledgeWithdrawNotice(sentence === "" ? notice : `${notice} ${sentence}`);
    } finally {
      setKnowledgeWithdrawSaving(false);
    }
  }

  function updateKnowledgeSource(key, value) {
    setKnowledgeSource((current) => ({ ...current, [key]: value }));
  }

  async function onChangeKnowledgeSource(event) {
    event.preventDefault();
    if (knowledgeSourceSaving) return;
    const command = resolveKnowledgeSourceCommand(knowledgeSourceCommand, knowledgeSource, () => ({
      commandId: crypto.randomUUID(),
      asOf: new Date().toISOString(),
    }));
    const built = buildKnowledgeSourceBody({
      commandId: command.commandId,
      asOf: command.asOf,
      fields: knowledgeSource,
    });
    if (built.error) {
      setKnowledgeSourceNotice("Source fields are incomplete or the review window is invalid.");
      return;
    }
    setKnowledgeSourceCommand(command);
    const tokenAtStart = getClientAuthToken();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    setKnowledgeSourceSaving(true);
    setKnowledgeSourceNotice("");
    try {
      let outcome = null;
      let jobId = "";
      try {
        const res = await axios.post(
          `${backendUrl}/api/roofing_v2/knowledge/${encodeURIComponent(built.body.fact.fact_id)}/source`,
          built.body,
          { headers: getClientAuthHeaders() },
        );
        if (getClientAuthToken() !== tokenAtStart) return;
        outcome = res.data && res.data.outcome;
        if (res.data && typeof res.data.job_id === "string") {
          jobId = res.data.job_id;
        }
      } catch (error) {
        if (getClientAuthToken() !== tokenAtStart) return;
        const status = error.response && error.response.status;
        const detail = error.response && error.response.data && error.response.data.detail;
        const followUp = knowledgeSourceSaveFollowUp({ status, detail });
        if (!followUp.reload) {
          setKnowledgeSourceNotice(followUp.notice);
          return;
        }
        try {
          const listed = await axios.get(`${backendUrl}/api/roofing_v2/knowledge`, {
            headers: getClientAuthHeaders(),
          });
          if (getClientAuthToken() !== tokenAtStart) return;
          setKnowledgeSourceNotice(followUp.notice);
          setKnowledge(roofingV2KnowledgeView(listed.data));
          setKnowledgeProblem("");
        } catch {
          if (getClientAuthToken() !== tokenAtStart) return;
          const failed = knowledgeSourceSaveFollowUp({ status, detail, reloadFailed: true });
          setKnowledgeSourceNotice(failed.notice);
        }
        return;
      }
      const accepted = knowledgeSourceSaveFollowUp({ outcome });
      if (!accepted.saved) {
        setKnowledgeSourceNotice(accepted.notice);
        return;
      }
      let notice = accepted.notice;
      try {
        const listed = await axios.get(`${backendUrl}/api/roofing_v2/knowledge`, {
          headers: getClientAuthHeaders(),
        });
        if (getClientAuthToken() !== tokenAtStart) return;
        const followUp = knowledgeSourceSaveFollowUp({ outcome });
        notice = followUp.notice;
        if (followUp.replaceList) {
          setKnowledge(roofingV2KnowledgeView(listed.data));
          setKnowledgeProblem("");
        }
      } catch {
        if (getClientAuthToken() !== tokenAtStart) return;
        notice = knowledgeSourceSaveFollowUp({ outcome, reloadFailed: true }).notice;
      }
      const sentence = await readInvalidationSentence(backendUrl, tokenAtStart, jobId);
      if (sentence == null) return;
      setKnowledgeSourceNotice(sentence === "" ? notice : `${notice} ${sentence}`);
    } finally {
      setKnowledgeSourceSaving(false);
    }
  }

  const columnLayout = settingsColumnLayout();
  const knowledgeShownAt = new Date().toISOString();

  return (
    <div className="min-h-screen bg-[#0F1115] flex">
      <Sidebar />
      <div className={columnLayout.frame}>
        {mode ? (
          <div className={columnLayout.column}>
            <h1 className="font-heading font-bold text-3xl mb-4">Roofing settings</h1>
            <p>booking_mode: {mode.booking_mode}</p>
            <p>{readinessNotice()}</p>
            <p>{readinessCheckTimeNotice()}</p>
            <p>{readinessExpiryNotice()}</p>
            <p>{readinessCandidateNotice()}</p>
            <p>{readinessAvailabilityNotice()}</p>
            <p>{readinessChatNotice()}</p>
            <p>{readinessSnapshotNotice()}</p>
            <p>{readinessProviderNotice()}</p>
            <p>{readinessAuditNotice()}</p>
            <p>{readinessMissingNotice()}</p>
            <p>{readinessAdmissionNotice()}</p>
            <p>{readinessRegisterNotice()}</p>
            <p>{readinessDistinctionNotice()}</p>
            <p>{readinessBypassNotice()}</p>
            <p>{readinessPauseNotice()}</p>
            {(readinessLines || []).map((line) => (
              <p key={line}>{line}</p>
            ))}
            <p>shows_automatic: {String(mode.shows_automatic)}</p>
            <p>reports_no_free_slots: {String(mode.reports_no_free_slots)}</p>
            <p>model_nlu_enabled: {mode.model_nlu_enabled ? "true" : "false"}</p>
            <ul className="mt-4 list-disc pl-5">
              {mode.reasons.map((reason, index) => (
                <li key={`${reasonCode(reason) || "reason"}:${index}`}>{reasonLine(reason)}</li>
              ))}
            </ul>
            <section className="mt-4">
              <h2 className="font-heading font-bold text-xl mb-2">Capabilities and roles</h2>
              <p>provider_capability_profile.calendar: {String(capability.calendar)}</p>
              <p>provider_capability_profile.model: {String(capability.model)}</p>
              <p>provider_capability_profile.provider: {String(capability.provider)}</p>
              <p>repair_ballpark: {moneyText(capability.repairBallpark, sharedCurrency)}</p>
              <p>attendee: {String(capability.attendee)}</p>
              <p>on_site_quoter_role: {String(capability.onSiteQuoterRole)}</p>
              <p>follow_up_booking_flow: {String(capability.followUpBookingFlow)}</p>
              <p>mandatory_ai_disclosure: {String(capability.mandatoryAiDisclosure)}</p>
            </section>
            <form
              className="mt-8 space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (!saving && !reloading) onSave();
              }}
            >
              <label className="block" htmlFor="roofing-v2-brand">
                Brand
                <input
                  id="roofing-v2-brand"
                  className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                  value={draft.brand}
                  onChange={(event) => setDraft({ ...draft, brand: event.target.value })}
                />
              </label>
              <label className="block" htmlFor="roofing-v2-tone">
                Tone
                <input
                  id="roofing-v2-tone"
                  className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                  value={draft.tone}
                  onChange={(event) => setDraft({ ...draft, tone: event.target.value })}
                />
              </label>
              <fieldset>
                <legend>Phrases</legend>
                {blankPhraseEntriesView.map((entry) => (
                  <p key={`${entry.key}:${entry.line}`}>
                    {entry.line}
                    <button
                      type="button"
                      aria-label={storedBlankPhraseRemoveLabel(entry.line)}
                      onClick={() => {
                        const marked = draft.omittedBlankKeys || [];
                        if (marked.includes(entry.key)) return;
                        setDraft({ ...draft, omittedBlankKeys: [...marked, entry.key] });
                      }}
                    >
                      Remove
                    </button>
                  </p>
                ))}
                {draft.rows.map((row, index) => {
                  const labels = phraseFieldLabels(index) || {
                    key: "Phrase key",
                    value: "Phrase value",
                    remove: "Remove",
                  };
                  const layout = phraseRowLayout();
                  return (
                  <div key={row.id} className={layout.row}>
                    <input
                      aria-label={labels.key}
                      className={layout.key}
                      value={row.key}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          rows: draft.rows.map((item) =>
                            item.id === row.id ? { ...item, key: event.target.value } : item
                          ),
                        })
                      }
                    />
                    <input
                      aria-label={labels.value}
                      className={layout.value}
                      value={row.value}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          rows: draft.rows.map((item) =>
                            item.id === row.id ? { ...item, value: event.target.value } : item
                          ),
                        })
                      }
                    />
                    <button
                      type="button"
                      aria-label={labels.remove}
                      className={layout.remove}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          rows: draft.rows.filter((item) => item.id !== row.id),
                        })
                      }
                    >
                      Remove
                    </button>
                  </div>
                  );
                })}
                <button
                  type="button"
                  className="mt-2"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      rows: [...draft.rows, { id: crypto.randomUUID(), key: "", value: "" }],
                    })
                  }
                >
                  Add phrase
                </button>
              </fieldset>
              <p>settings_revision: {revisionText(snapshot.settings && snapshot.settings.settings_revision)}</p>
              <p>dispatch_policy_revision: {revisionText(snapshot.settings && snapshot.settings.dispatch_policy_revision)}</p>
              <p>schema_version: {schemaVersionText(snapshot.settings)}</p>
              <p>spec_revision: {specRevisionText(snapshot.settings)}</p>
              <p>bot_type: {botTypeText(snapshot.settings)}</p>
              <p>user_id: {userIdText(snapshot.settings)}</p>
              <div ref={noticeRef} role="status" aria-live="polite" tabIndex={-1}>
                {notice ? <p>{notice}</p> : null}
                {reloadProblem ? <p>{reloadProblem}</p> : null}
                {progressLabel ? <p>{progressLabel}</p> : null}
              </div>
              <button type="submit" disabled={saving || reloading} className="rounded bg-[#00D1FF] px-4 py-2 text-[#0F1115]">
                Save
              </button>
              {notice === "settings_revision conflict" ? (
                <button
                  type="button"
                  disabled={saving || reloading}
                  className="ml-3 rounded px-4 py-2"
                  onClick={onReload}
                >
                  Reload
                </button>
              ) : null}
              {needsReset ? (
                <button
                  type="button"
                  disabled={saving || reloading}
                  className="ml-3 rounded px-4 py-2"
                  onClick={() => {
                    if (saving || reloading) return;
                    setDraft(draftFromSettings(snapshot.settings));
                  }}
                >
                  Reset
                </button>
              ) : null}
              {changeNames.length > 0 ? <p>Unsaved changes: {changeNames.join(", ")}</p> : null}
              {changeDetails.map((detail, index) => (
                <p key={`${detail.name}:${index}`}>{metadataChangeLine(detail)}</p>
              ))}
            </form>
            <section className="mt-8">
              <h2 className="font-heading font-bold text-xl mb-2">Schedule</h2>
              <p>timezone: {String(schedule.timezone)}</p>
              <p>travel_buffer_minutes: {String(schedule.travelBufferMinutes)}</p>
              <p>appointment_time_format: {String(schedule.appointmentTimeFormat)}</p>
              <p>window_size_hours: {String(schedule.windowSizeHours)}</p>
              <p>window_size_hours is not the visit duration.</p>
              <p>create_min_notice_minutes: {String(schedule.createMinNoticeMinutes)}</p>
              <p>reschedule_min_notice_minutes: {String(schedule.rescheduleMinNoticeMinutes)}</p>
              <p>resource_ref: {String(schedule.resourceRef)}</p>
              <p>
                calendar_ref: {schedule.calendarRef === null ? "null" : String(schedule.calendarRef)}
              </p>
              {schedule.skipWeekdaysStatus ? (
                <p>skip_weekdays: {schedule.skipWeekdaysStatus}</p>
              ) : (
                <p>skip_weekdays: {schedule.skipWeekdays.map(weekdayLabel).join(", ")}</p>
              )}
              {scheduleTimeNotices().map((sentence) => (
                <p key={sentence}>{sentence}</p>
              ))}
              <p>{scheduleSendNotice()}</p>
              <p>{scheduleReopenNotice()}</p>
              <p>{scheduleEmergencyNotice()}</p>
              <p>{scheduleExpiryNotice()}</p>
              <p>office_hours.always_on: {String(schedule.officeHours.alwaysOn)}</p>
              {officeWindowsHeading ? <p>{officeWindowsHeading}</p> : schedule.officeHours.windows.map((item) => (
                <p key={`office-${item.index}`}>
                  office weekday {weekdayLabel(item.weekday)} {String(item.startLocal)}–{String(item.endLocal)}
                </p>
              ))}
              <p>bot_schedule.always_on: {String(schedule.botSchedule.alwaysOn)}</p>
              {botWindowsHeading ? <p>{botWindowsHeading}</p> : schedule.botSchedule.windows.map((item) => (
                <p key={`bot-${item.index}`}>
                  bot weekday {weekdayLabel(item.weekday)} {String(item.startLocal)}–{String(item.endLocal)}
                </p>
              ))}
              {appointmentTypesHeading ? <p>{appointmentTypesHeading}</p> : schedule.appointmentTypes.map((item) => (
                <div key={item.id} className="mt-3">
                  <p>{item.id}</p>
                  <p>duration_minutes: {String(item.durationMinutes)}</p>
                  <p>
                    is_free: {item.isFree === true ? "free" : String(item.isFree)}
                  </p>
                  <p>price: {moneyText(item.price, item.currency)}</p>
                  <p>currency: {String(item.currency)}</p>
                  <p>photos_required: {String(item.photosRequired)}</p>
                </div>
              ))}
            </section>
            <section className="mt-8">
              <h2 className="font-heading font-bold text-xl mb-2">Services and area</h2>
              {servicesHeading ? <p>{servicesHeading}</p> : serviceArea.services.map((item) => (
                <p key={item.index}>
                  {String(item.serviceId)}: {String(item.state)}
                </p>
              ))}
              <p>regional_callouts.allowed: {String(serviceArea.calloutsAllowed)}</p>
              <p>regional_callouts.min_job_value: {moneyText(serviceArea.minJobValue, sharedCurrency)}</p>
              <p>crosses_state_borders: {String(serviceArea.crossesStateBorders)}</p>
            </section>
            <section className="mt-8">
              <h2 className="font-heading font-bold text-xl mb-2">Qualification</h2>
              <p>strict_qualification_gate: {String(qualification.strictQualificationGate)}</p>
              {questionsHeading ? <p>{questionsHeading}</p> : qualification.questions.map((question) => {
                const activationHeading = configuredListHeading(
                  "activation",
                  question.activationStatus,
                  question.activation,
                );
                return (
                  <div key={question.index} className="mt-3">
                    <p>key: {String(question.key)}</p>
                    <p>enabled: {String(question.enabled)}</p>
                    <p>mandatory: {String(question.mandatory)}</p>
                    <p>
                      depends_on: {activationValueText(question.dependsOn)}
                    </p>
                    {activationHeading ? <p>{activationHeading}</p> : question.activation.map((clause) => (
                      <p key={clause.index}>
                        {String(clause.factKey)} {String(clause.op)}{" "}
                        {activationValueText(clause.value)}
                      </p>
                    ))}
                  </div>
                );
              })}
              {rulesHeading ? <p>{rulesHeading}</p> : qualification.rules.map((rule) => (
                <p key={rule.index}>
                  {String(rule.ruleId)}: {String(rule.enabled)}; requires_keys: {activationValueText(rule.requiresKeys)}
                </p>
              ))}
              <p>min_budget: {moneyText(qualification.minBudget, sharedCurrency)}</p>
              <p>max_storeys: {String(qualification.maxStoreys)}</p>
              {qualification.blockedPropertyTypesStatus ? (
                <p>blocked_property_types: {qualification.blockedPropertyTypesStatus}</p>
              ) : (
                <p>blocked_property_types: {qualification.blockedPropertyTypes.map(activationValueText).join(", ")}</p>
              )}
              <p>tenant_policy: {String(qualification.tenantPolicy)}</p>
              <p>asbestos_policy: {String(qualification.asbestosPolicy)}</p>
              <p>unqualified_action: {String(qualification.unqualifiedAction)}</p>
              {rejectionMessagesHeading ? (
                <p>{rejectionMessagesHeading}</p>
              ) : qualification.rejectionMessages.map((item) => (
                <p key={item.key}>
                  {activationValueText(item.key)}: {item.value === "" ? '""' : item.value}
                </p>
              ))}
            </section>
            <section className="mt-8">
              <h2 className="font-heading font-bold text-xl mb-2">Handoff</h2>
              <p>{handoffDeadlineNotice()}</p>
              <p>{handoffCancelNotice()}</p>
              {targetsHeading ? <p>{targetsHeading}</p> : handoff.targets.map((item, index) => (
                <p key={`${index}:${item}`}>target: {activationValueText(item)}</p>
              ))}
              {triggersHeading ? <p>{triggersHeading}</p> : handoff.triggers.map((item, index) => (
                <p key={`${index}:${item}`}>trigger: {activationValueText(item)}</p>
              ))}
              {routesHeading ? <p>{routesHeading}</p> : handoff.escalationRoutes.map((item, index) => (
                <p key={`${index}:${item}`}>escalation_route: {activationValueText(item)}</p>
              ))}
              {messagesHeading ? <p>{messagesHeading}</p> : handoff.messages.map((item) => (
                <p key={item.key}>
                  {activationValueText(item.key)}: {item.value === "" ? '""' : item.value}
                </p>
              ))}
              <p>emergency_contact_ref: {String(handoff.emergencyContactRef)}</p>
              <p>operator_contact_ref: {String(handoff.operatorContactRef)}</p>
              <p>operator_hours.always_on: {String(handoff.alwaysOn)}</p>
              {windowsHeading ? <p>{windowsHeading}</p> : handoff.windows.map((item) => (
                <p key={item.index}>
                  weekday {weekdayLabel(item.weekday)} {String(item.startLocal)}–{String(item.endLocal)}
                </p>
              ))}
            </section>
            <section className="mt-8">
              <h2 className="font-heading font-bold text-xl mb-2">Media and weather</h2>
              {mediaLimitsHeading ? <p>{mediaLimitsHeading}</p> : media.limits.map((item) => (
                <p key={item.key}>
                  {activationValueText(item.key)}: {String(item.value)}
                </p>
              ))}
              <p>reviewed_waiver_permissions: {String(media.reviewedWaiverPermissions)}</p>
              <p>weather_policy: {String(media.weatherPolicy)}</p>
            </section>
            <section className="mt-8">
              <h2 className="font-heading font-bold text-xl mb-2">Channel and notifications</h2>
              <p>confirmation_mode: {String(channel.confirmationMode)}</p>
              {capabilitiesHeading ? <p>{capabilitiesHeading}</p> : channel.capabilities.map((item) => (
                <p key={item.key}>
                  {activationValueText(item.key)}: {String(item.value)}
                </p>
              ))}
            </section>
            <section className="mt-8">
              <h2 className="font-heading font-bold text-xl mb-2">Data and budgets</h2>
              <p>release_slo.profile_id: {releaseSloText(snapshot.settings)}</p>
              <p>retention_class: {String(dataProfile.retentionClass)}</p>
              <p>region: {String(dataProfile.region)}</p>
              <p>opt_out: {String(dataProfile.optOut)}</p>
              {budgetsHeading ? <p>{budgetsHeading}</p> : dataProfile.budgets.map((item) => (
                <p key={item.key}>
                  {activationValueText(item.key)}: {String(item.value)}
                </p>
              ))}
            </section>
            <section className="mt-8">
              <h2 className="font-heading font-bold text-xl mb-2">Knowledge</h2>
              <form className="mb-4 space-y-4" onSubmit={onSaveKnowledgeDraft}>
                <label className="block" htmlFor="roofing-v2-knowledge-fact-id">
                  fact_id
                  <input
                    id="roofing-v2-knowledge-fact-id"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeDraft.factId}
                    onChange={(event) => updateKnowledgeDraft("factId", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-knowledge-revision">
                  revision
                  <input
                    id="roofing-v2-knowledge-revision"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeDraft.revision}
                    onChange={(event) => updateKnowledgeDraft("revision", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-knowledge-topic">
                  topic_key
                  <input
                    id="roofing-v2-knowledge-topic"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeDraft.topicKey}
                    onChange={(event) => updateKnowledgeDraft("topicKey", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-knowledge-text">
                  approved_text_or_typed_value
                  <input
                    id="roofing-v2-knowledge-text"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeDraft.text}
                    onChange={(event) => updateKnowledgeDraft("text", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-knowledge-language">
                  language
                  <input
                    id="roofing-v2-knowledge-language"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeDraft.language}
                    onChange={(event) => updateKnowledgeDraft("language", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-knowledge-audience">
                  audience
                  <select
                    id="roofing-v2-knowledge-audience"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeDraft.audience}
                    onChange={(event) => updateKnowledgeDraft("audience", event.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="customer">customer</option>
                    <option value="internal">internal</option>
                  </select>
                </label>
                <label className="block" htmlFor="roofing-v2-knowledge-owner">
                  owner_ref
                  <input
                    id="roofing-v2-knowledge-owner"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeDraft.ownerRef}
                    onChange={(event) => updateKnowledgeDraft("ownerRef", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-knowledge-expected">
                  expected_revision
                  <input
                    id="roofing-v2-knowledge-expected"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeDraft.expectedRevision}
                    onChange={(event) => updateKnowledgeDraft("expectedRevision", event.target.value)}
                  />
                </label>
                <button
                  type="submit"
                  className="rounded bg-[#00D1FF] px-4 py-2 text-[#0F1115]"
                  disabled={knowledgeDraftSaving}
                >
                  Save draft
                </button>
                {knowledgeDraftNotice ? <p>{knowledgeDraftNotice}</p> : null}
              </form>
              <form className="mb-4 space-y-4" onSubmit={onPublishKnowledge}>
                <label className="block" htmlFor="roofing-v2-publish-fact-id">
                  fact_id
                  <input
                    id="roofing-v2-publish-fact-id"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.factId}
                    onChange={(event) => updateKnowledgePublish("factId", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-revision">
                  revision
                  <input
                    id="roofing-v2-publish-revision"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.revision}
                    onChange={(event) => updateKnowledgePublish("revision", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-topic">
                  topic_key
                  <input
                    id="roofing-v2-publish-topic"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.topicKey}
                    onChange={(event) => updateKnowledgePublish("topicKey", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-text">
                  approved_text_or_typed_value
                  <input
                    id="roofing-v2-publish-text"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.text}
                    onChange={(event) => updateKnowledgePublish("text", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-language">
                  language
                  <input
                    id="roofing-v2-publish-language"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.language}
                    onChange={(event) => updateKnowledgePublish("language", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-audience">
                  audience
                  <select
                    id="roofing-v2-publish-audience"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.audience}
                    onChange={(event) => updateKnowledgePublish("audience", event.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="customer">customer</option>
                    <option value="internal">internal</option>
                  </select>
                </label>
                <label className="block" htmlFor="roofing-v2-publish-owner">
                  owner_ref
                  <input
                    id="roofing-v2-publish-owner"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.ownerRef}
                    onChange={(event) => updateKnowledgePublish("ownerRef", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-source">
                  source_ref
                  <input
                    id="roofing-v2-publish-source"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.sourceRef}
                    onChange={(event) => updateKnowledgePublish("sourceRef", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-source-revision">
                  source_revision
                  <input
                    id="roofing-v2-publish-source-revision"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.sourceRevision}
                    onChange={(event) => updateKnowledgePublish("sourceRevision", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-approved-by">
                  approved_by
                  <input
                    id="roofing-v2-publish-approved-by"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.approvedBy}
                    onChange={(event) => updateKnowledgePublish("approvedBy", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-reviewer">
                  reviewer_ref
                  <input
                    id="roofing-v2-publish-reviewer"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.reviewerRef}
                    onChange={(event) => updateKnowledgePublish("reviewerRef", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-approved-at">
                  approved_at
                  <input
                    id="roofing-v2-publish-approved-at"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.approvedAt}
                    onChange={(event) => updateKnowledgePublish("approvedAt", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-valid-from">
                  valid_from
                  <input
                    id="roofing-v2-publish-valid-from"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.validFrom}
                    onChange={(event) => updateKnowledgePublish("validFrom", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-valid-until">
                  valid_until
                  <input
                    id="roofing-v2-publish-valid-until"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.validUntil}
                    onChange={(event) => updateKnowledgePublish("validUntil", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-publish-expected">
                  expected_revision
                  <input
                    id="roofing-v2-publish-expected"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgePublish.expectedRevision}
                    onChange={(event) => updateKnowledgePublish("expectedRevision", event.target.value)}
                  />
                </label>
                <button
                  type="submit"
                  className="rounded bg-[#00D1FF] px-4 py-2 text-[#0F1115]"
                  disabled={knowledgePublishSaving}
                >
                  Publish
                </button>
                {knowledgePublishNotice ? <p>{knowledgePublishNotice}</p> : null}
              </form>
              <form className="mb-4 space-y-4" onSubmit={onWithdrawKnowledge}>
                <label className="block" htmlFor="roofing-v2-withdraw-fact-id">
                  fact_id
                  <input
                    id="roofing-v2-withdraw-fact-id"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.factId}
                    onChange={(event) => updateKnowledgeWithdraw("factId", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-withdraw-revision">
                  revision
                  <input
                    id="roofing-v2-withdraw-revision"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.revision}
                    onChange={(event) => updateKnowledgeWithdraw("revision", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-withdraw-topic">
                  topic_key
                  <input
                    id="roofing-v2-withdraw-topic"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.topicKey}
                    onChange={(event) => updateKnowledgeWithdraw("topicKey", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-withdraw-text">
                  approved_text_or_typed_value
                  <input
                    id="roofing-v2-withdraw-text"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.text}
                    onChange={(event) => updateKnowledgeWithdraw("text", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-withdraw-language">
                  language
                  <input
                    id="roofing-v2-withdraw-language"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.language}
                    onChange={(event) => updateKnowledgeWithdraw("language", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-withdraw-audience">
                  audience
                  <select
                    id="roofing-v2-withdraw-audience"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.audience}
                    onChange={(event) => updateKnowledgeWithdraw("audience", event.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="customer">customer</option>
                    <option value="internal">internal</option>
                  </select>
                </label>
                <label className="block" htmlFor="roofing-v2-withdraw-owner">
                  owner_ref
                  <input
                    id="roofing-v2-withdraw-owner"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.ownerRef}
                    onChange={(event) => updateKnowledgeWithdraw("ownerRef", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-withdraw-source">
                  source_ref
                  <input
                    id="roofing-v2-withdraw-source"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.sourceRef}
                    onChange={(event) => updateKnowledgeWithdraw("sourceRef", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-withdraw-source-revision">
                  source_revision
                  <input
                    id="roofing-v2-withdraw-source-revision"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.sourceRevision}
                    onChange={(event) => updateKnowledgeWithdraw("sourceRevision", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-withdraw-expected">
                  expected_revision
                  <input
                    id="roofing-v2-withdraw-expected"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeWithdraw.expectedRevision}
                    onChange={(event) => updateKnowledgeWithdraw("expectedRevision", event.target.value)}
                  />
                </label>
                <button
                  type="submit"
                  className="rounded bg-[#00D1FF] px-4 py-2 text-[#0F1115]"
                  disabled={knowledgeWithdrawSaving}
                >
                  Withdraw
                </button>
                {knowledgeWithdrawNotice ? <p>{knowledgeWithdrawNotice}</p> : null}
              </form>
              <form className="mb-4 space-y-4" onSubmit={onChangeKnowledgeSource}>
                <label className="block" htmlFor="roofing-v2-source-fact-id">
                  fact_id
                  <input
                    id="roofing-v2-source-fact-id"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.factId}
                    onChange={(event) => updateKnowledgeSource("factId", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-revision">
                  revision
                  <input
                    id="roofing-v2-source-revision"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.revision}
                    onChange={(event) => updateKnowledgeSource("revision", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-topic">
                  topic_key
                  <input
                    id="roofing-v2-source-topic"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.topicKey}
                    onChange={(event) => updateKnowledgeSource("topicKey", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-text">
                  approved_text_or_typed_value
                  <input
                    id="roofing-v2-source-text"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.text}
                    onChange={(event) => updateKnowledgeSource("text", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-language">
                  language
                  <input
                    id="roofing-v2-source-language"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.language}
                    onChange={(event) => updateKnowledgeSource("language", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-audience">
                  audience
                  <select
                    id="roofing-v2-source-audience"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.audience}
                    onChange={(event) => updateKnowledgeSource("audience", event.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="customer">customer</option>
                    <option value="internal">internal</option>
                  </select>
                </label>
                <label className="block" htmlFor="roofing-v2-source-owner">
                  owner_ref
                  <input
                    id="roofing-v2-source-owner"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.ownerRef}
                    onChange={(event) => updateKnowledgeSource("ownerRef", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-ref">
                  source_ref
                  <input
                    id="roofing-v2-source-ref"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.sourceRef}
                    onChange={(event) => updateKnowledgeSource("sourceRef", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-source-revision">
                  source_revision
                  <input
                    id="roofing-v2-source-source-revision"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.sourceRevision}
                    onChange={(event) => updateKnowledgeSource("sourceRevision", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-approved-by">
                  approved_by
                  <input
                    id="roofing-v2-source-approved-by"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.approvedBy}
                    onChange={(event) => updateKnowledgeSource("approvedBy", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-reviewer">
                  reviewer_ref
                  <input
                    id="roofing-v2-source-reviewer"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.reviewerRef}
                    onChange={(event) => updateKnowledgeSource("reviewerRef", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-approved-at">
                  approved_at
                  <input
                    id="roofing-v2-source-approved-at"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.approvedAt}
                    onChange={(event) => updateKnowledgeSource("approvedAt", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-valid-from">
                  valid_from
                  <input
                    id="roofing-v2-source-valid-from"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.validFrom}
                    onChange={(event) => updateKnowledgeSource("validFrom", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-valid-until">
                  valid_until
                  <input
                    id="roofing-v2-source-valid-until"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.validUntil}
                    onChange={(event) => updateKnowledgeSource("validUntil", event.target.value)}
                  />
                </label>
                <label className="block" htmlFor="roofing-v2-source-expected">
                  expected_revision
                  <input
                    id="roofing-v2-source-expected"
                    className="mt-1 w-full rounded bg-[#1F2937] px-3 py-2"
                    value={knowledgeSource.expectedRevision}
                    onChange={(event) => updateKnowledgeSource("expectedRevision", event.target.value)}
                  />
                </label>
                <button
                  type="submit"
                  className="rounded bg-[#00D1FF] px-4 py-2 text-[#0F1115]"
                  disabled={knowledgeSourceSaving}
                >
                  Change source
                </button>
                {knowledgeSourceNotice ? <p>{knowledgeSourceNotice}</p> : null}
              </form>
              {knowledgeProblem ? <p>{knowledgeProblem}</p> : null}
              {!knowledgeProblem && knowledge && knowledge.status === "not configured" ? (
                <p>not configured</p>
              ) : null}
              {!knowledgeProblem && knowledge && knowledge.status == null
                ? knowledge.rows.map((row, index) => (
                  <div key={`${row.fact_id}:${row.revision}:${index}`}>
                    <p>fact_id: {String(row.fact_id)}</p>
                    <p>revision: {String(row.revision)}</p>
                    <p>status: {String(row.status)}</p>
                    <p>window: {knowledgeWindowLabel(row, knowledgeShownAt)}</p>
                    <p>customer_preview: {knowledgeCustomerPreview(row, knowledgeShownAt)}</p>
                    <p>audience: {String(row.audience)}</p>
                    <p>topic_key: {String(row.topic_key)}</p>
                    <p>approved_text_or_typed_value: {String(row.approved_text_or_typed_value)}</p>
                    <p>source_ref: {String(row.source_ref)}</p>
                    <p>source_revision: {String(row.source_revision)}</p>
                    <p>owner_ref: {String(row.owner_ref)}</p>
                    <p>reviewer_ref: {String(row.reviewer_ref)}</p>
                    <p>valid_from: {String(row.valid_from)}</p>
                    <p>valid_until: {String(row.valid_until)}</p>
                  </div>
                ))
                : null}
              {!knowledgeProblem && knowledge && knowledge.status == null
                ? knowledge.conflicts.map((conflict, index) => (
                  <div key={`${conflict.subject_ref}:${conflict.subject_version}:${index}`}>
                    <p>conflict subject_ref: {String(conflict.subject_ref)}</p>
                    <p>conflict subject_version: {String(conflict.subject_version)}</p>
                    <p>conflict status: {String(conflict.status)}</p>
                  </div>
                ))
                : null}
              {!knowledgeProblem && knowledge && knowledge.status == null
                ? (knowledge.in_flight_sends.length === 0
                  ? <p>No in-flight send is recorded.</p>
                  : knowledge.in_flight_sends.map((send, index) => (
                    <div key={`${send.outbound_id}:${send.status}:${index}`}>
                      <p>in-flight outbound_id: {String(send.outbound_id)}</p>
                      <p>in-flight status: {String(send.status)}</p>
                    </div>
                  )))
                : null}
            </section>
          </div>
        ) : (
          <p className="text-[#F3F4F6] font-body">The server response was not accepted.</p>
        )}
      </div>
    </div>
  );
}
